import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Store, Users, Shield, Clock, Globe, CreditCard, Wifi,
  WifiOff, Activity, TrendingUp, Eye, Search, Download,
  CheckCircle2, XCircle, AlertTriangle, Timer, Smartphone
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, AreaChart, Area, Legend
} from "recharts";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  restaurants: any[];
  plans: any[];
  orders: any[];
  subscriptions: any[];
}

const formatCurrency = (v: number) =>
  `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "11px",
  color: "hsl(var(--foreground))",
};

const COLORS = [
  "hsl(24, 95%, 53%)", "hsl(142, 71%, 45%)", "hsl(38, 92%, 50%)",
  "hsl(200, 80%, 50%)", "hsl(280, 60%, 50%)", "hsl(0, 72%, 51%)",
  "hsl(160, 60%, 45%)", "hsl(320, 70%, 50%)"
];

const AdminAccountsReportTab = ({ restaurants, plans, orders, subscriptions }: Props) => {
  const [loginLogs, setLoginLogs] = useState<any[]>([]);
  const [pageVisits, setPageVisits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "inactive">("all");
  const [filterPlan, setFilterPlan] = useState<"all" | "free" | "paid" | "trial">("all");

  useEffect(() => {
    const fetchData = async () => {
      const [logsRes, visitsRes] = await Promise.all([
        supabase.from("user_login_logs").select("*").order("logged_in_at", { ascending: false }).limit(1000),
        supabase.from("page_visits").select("*").order("created_at", { ascending: false }).limit(1000),
      ]);
      setLoginLogs(logsRes.data || []);
      setPageVisits(visitsRes.data || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  // ── Computed metrics ──

  const activeCount = restaurants.filter(r => r.is_active).length;
  const inactiveCount = restaurants.filter(r => !r.is_active).length;

  const planBreakdown = useMemo(() => {
    const free = restaurants.filter(r => !r.plan_id).length;
    const planMap: Record<string, number> = {};
    restaurants.forEach(r => {
      if (r.plan_id) {
        const name = plans.find(p => p.id === r.plan_id)?.name || "Desconhecido";
        planMap[name] = (planMap[name] || 0) + 1;
      }
    });
    const result = [{ name: "Sem plano (Gratuito)", value: free }];
    Object.entries(planMap).forEach(([name, value]) => result.push({ name, value }));
    return result;
  }, [restaurants, plans]);

  const trialStats = useMemo(() => {
    const usedTrial = restaurants.filter(r => r.trial_used).length;
    const neverUsed = restaurants.filter(r => !r.trial_used).length;
    const onTrial = restaurants.filter(r => r.trial_started_at && r.trial_ends_at && new Date(r.trial_ends_at) > new Date()).length;
    const trialExpired = restaurants.filter(r => r.trial_started_at && r.trial_ends_at && new Date(r.trial_ends_at) <= new Date()).length;
    return { usedTrial, neverUsed, onTrial, trialExpired };
  }, [restaurants]);

  const trialPieData = [
    { name: "Em trial ativo", value: trialStats.onTrial },
    { name: "Trial expirado", value: trialStats.trialExpired },
    { name: "Nunca usou trial", value: trialStats.neverUsed },
    { name: "Já usou trial", value: trialStats.usedTrial - trialStats.onTrial - trialStats.trialExpired },
  ].filter(d => d.value > 0);

  const statusPieData = [
    { name: "Ativas", value: activeCount },
    { name: "Inativas", value: inactiveCount },
  ];

  // Payment methods analysis from orders
  const paymentMethodsData = useMemo(() => {
    const methods: Record<string, number> = {};
    orders.forEach(o => {
      const method = o.payment_method || "não informado";
      methods[method] = (methods[method] || 0) + 1;
    });
    return Object.entries(methods)
      .map(([name, value]) => ({ name: formatPaymentMethod(name), value }))
      .sort((a, b) => b.value - a.value);
  }, [orders]);

  // Payment methods per restaurant
  const paymentMethodsByRestaurant = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    orders.forEach(o => {
      if (!map[o.restaurant_id]) map[o.restaurant_id] = {};
      const method = o.payment_method || "não informado";
      map[o.restaurant_id][method] = (map[o.restaurant_id][method] || 0) + 1;
    });
    return map;
  }, [orders]);

  // Last login per user
  const lastLoginByUser = useMemo(() => {
    const map: Record<string, { logged_in_at: string; ip_address: string | null; user_agent: string | null }> = {};
    loginLogs.forEach(log => {
      if (!map[log.user_id] || log.logged_in_at > map[log.user_id].logged_in_at) {
        map[log.user_id] = { logged_in_at: log.logged_in_at, ip_address: log.ip_address, user_agent: log.user_agent };
      }
    });
    return map;
  }, [loginLogs]);

  // Daily activity (page visits per day, last 14 days)
  const dailyActivityData = useMemo(() => {
    const days: { date: string; label: string; visits: number; uniqueUsers: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const dayVisits = pageVisits.filter(v => v.created_at?.slice(0, 10) === iso);
      const uniqueUsers = new Set(dayVisits.map(v => v.user_id).filter(Boolean)).size;
      days.push({
        date: iso,
        label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        visits: dayVisits.length,
        uniqueUsers,
      });
    }
    return days;
  }, [pageVisits]);

  // Logins per day (last 14 days)
  const loginsPerDay = useMemo(() => {
    const days: { date: string; label: string; logins: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const dayLogins = loginLogs.filter(l => l.logged_in_at?.slice(0, 10) === iso);
      days.push({
        date: iso,
        label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        logins: dayLogins.length,
      });
    }
    return days;
  }, [loginLogs]);

  // New registrations per week (last 8 weeks)
  const registrationTrend = useMemo(() => {
    const weeks: { label: string; count: number }[] = [];
    for (let i = 7; i >= 0; i--) {
      const start = new Date();
      start.setDate(start.getDate() - (i + 1) * 7);
      const end = new Date();
      end.setDate(end.getDate() - i * 7);
      const count = restaurants.filter(r => {
        const d = new Date(r.created_at);
        return d >= start && d < end;
      }).length;
      weeks.push({
        label: `Sem ${8 - i}`,
        count,
      });
    }
    return weeks;
  }, [restaurants]);

  // Filtered restaurant list
  const filteredRestaurants = useMemo(() => {
    return restaurants.filter(r => {
      const matchSearch = !search ||
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.slug.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === "all" ||
        (filterStatus === "active" && r.is_active) ||
        (filterStatus === "inactive" && !r.is_active);
      const matchPlan = filterPlan === "all" ||
        (filterPlan === "free" && !r.plan_id) ||
        (filterPlan === "paid" && r.plan_id && !r.trial_started_at) ||
        (filterPlan === "trial" && r.trial_started_at && r.trial_ends_at && new Date(r.trial_ends_at) > new Date());
      return matchSearch && matchStatus && matchPlan;
    });
  }, [restaurants, search, filterStatus, filterPlan]);

  // CSV Export
  const exportCSV = () => {
    const headers = ["Nome", "Slug", "Status", "Plano", "Trial Usado", "Último Login", "Último IP", "Método Pagamento Favorito", "Criado Em"];
    const rows = filteredRestaurants.map(r => {
      const loginInfo = lastLoginByUser[r.owner_id];
      const methods = paymentMethodsByRestaurant[r.id] || {};
      const topMethod = Object.entries(methods).sort(([, a], [, b]) => (b as number) - (a as number))[0];
      return [
        r.name,
        r.slug,
        r.is_active ? "Ativo" : "Inativo",
        plans.find(p => p.id === r.plan_id)?.name || "Gratuito",
        r.trial_used ? "Sim" : "Não",
        loginInfo?.logged_in_at ? new Date(loginInfo.logged_in_at).toLocaleString("pt-BR") : "—",
        loginInfo?.ip_address || "—",
        topMethod ? formatPaymentMethod(topMethod[0]) : "—",
        new Date(r.created_at).toLocaleDateString("pt-BR"),
      ].join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-contas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const cardClass = "rounded-xl border border-border bg-card p-5";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Relatório de Contas
          </h2>
          <p className="text-xs text-muted-foreground">Visão completa de todas as contas de restaurantes na plataforma</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="text-xs">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Exportar CSV
        </Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: "Total Contas", value: restaurants.length, icon: Store, color: "text-primary" },
          { label: "Ativas", value: activeCount, icon: CheckCircle2, color: "text-emerald-500" },
          { label: "Inativas", value: inactiveCount, icon: XCircle, color: "text-red-500" },
          { label: "Plano Gratuito", value: restaurants.filter(r => !r.plan_id).length, icon: Users, color: "text-muted-foreground" },
          { label: "Planos Pagos", value: restaurants.filter(r => r.plan_id).length, icon: CreditCard, color: "text-amber-500" },
          { label: "Em Trial", value: trialStats.onTrial, icon: Timer, color: "text-blue-500" },
        ].map((kpi, i) => (
          <motion.div
            key={kpi.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className={cardClass}
          >
            <kpi.icon className={`h-4 w-4 ${kpi.color} mb-2`} />
            <p className="text-2xl font-bold font-display">{kpi.value}</p>
            <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row 1: Status + Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Active vs Inactive Pie */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-3">Status das Contas</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                {statusPieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {statusPieData.map((d, i) => (
              <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </motion.div>

        {/* Plan breakdown Pie */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-3">Distribuição por Plano</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={planBreakdown} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                {planBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
            {planBreakdown.map((d, i) => (
              <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </motion.div>

        {/* Trial usage Pie */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-3">Uso do Trial</h3>
          {trialPieData.length === 0 ? (
            <div className="flex items-center justify-center h-[180px] text-muted-foreground text-xs">Sem dados</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={trialPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {trialPieData.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
                {trialPieData.map((d, i) => (
                  <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[(i + 3) % COLORS.length] }} />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Charts Row 2: Activity + Logins + Registrations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Activity */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-1">Atividade Diária (14 dias)</h3>
          <p className="text-[10px] text-muted-foreground mb-3">Visitas e usuários únicos por dia</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={dailyActivityData}>
              <defs>
                <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis hide />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="visits" stroke="hsl(24, 95%, 53%)" strokeWidth={2} fill="url(#visitGrad)" name="Visitas" />
              <Area type="monotone" dataKey="uniqueUsers" stroke="hsl(142, 71%, 45%)" strokeWidth={1.5} fill="transparent" name="Usuários Únicos" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Logins per day */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-1">Logins Diários (14 dias)</h3>
          <p className="text-[10px] text-muted-foreground mb-3">Quantidade de logins realizados por dia</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={loginsPerDay}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis hide />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="logins" fill="hsl(200, 80%, 50%)" radius={[4, 4, 0, 0]} name="Logins" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Charts Row 3: Payment Methods + Registration Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payment methods */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-1">Meios de Pagamento Mais Usados</h3>
          <p className="text-[10px] text-muted-foreground mb-3">Distribuição geral de métodos de pagamento nos pedidos</p>
          {paymentMethodsData.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-xs">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={paymentMethodsData} layout="vertical">
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={120} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" fill="hsl(38, 92%, 50%)" radius={[0, 4, 4, 0]} name="Pedidos" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Registration trend */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className={cardClass}>
          <h3 className="font-display font-semibold text-sm mb-1">Tendência de Cadastros (8 semanas)</h3>
          <p className="text-[10px] text-muted-foreground mb-3">Novos restaurantes cadastrados por semana</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={registrationTrend}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis hide />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="hsl(280, 60%, 50%)" radius={[4, 4, 0, 0]} name="Cadastros" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Detailed Table */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className={cardClass}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h3 className="font-display font-semibold text-sm">Detalhes por Conta</h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs w-44 bg-background"
              />
            </div>
            <Select value={filterStatus} onValueChange={(v: any) => setFilterStatus(v)}>
              <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlan} onValueChange={(v: any) => setFilterPlan(v)}>
              <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Planos</SelectItem>
                <SelectItem value="free">Gratuito</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="trial">Em Trial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left p-3 font-medium">Restaurante</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Plano</th>
                <th className="text-left p-3 font-medium">Trial</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Último Login</th>
                <th className="text-left p-3 font-medium hidden lg:table-cell">Último IP</th>
                <th className="text-left p-3 font-medium hidden xl:table-cell">Pgto Favorito</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Pedidos</th>
                <th className="text-left p-3 font-medium hidden lg:table-cell">Faturamento</th>
                <th className="text-left p-3 font-medium">Criado</th>
              </tr>
            </thead>
            <tbody>
              {filteredRestaurants.map(r => {
                const loginInfo = lastLoginByUser[r.owner_id];
                const restOrders = orders.filter(o => o.restaurant_id === r.id);
                const revenue = restOrders.reduce((s: number, o: any) => s + Number(o.total), 0);
                const methods = paymentMethodsByRestaurant[r.id] || {};
                const topMethod = Object.entries(methods).sort(([, a], [, b]) => (b as number) - (a as number))[0];
                const planName = plans.find(p => p.id === r.plan_id)?.name || "Gratuito";
                const isOnTrial = r.trial_started_at && r.trial_ends_at && new Date(r.trial_ends_at) > new Date();
                const trialDaysLeft = isOnTrial ? Math.max(0, Math.ceil((new Date(r.trial_ends_at).getTime() - Date.now()) / 86400000)) : null;

                return (
                  <tr key={r.id} className={`border-b border-border/50 hover:bg-secondary/30 ${!r.is_active ? "opacity-50" : ""}`}>
                    <td className="p-3">
                      <p className="font-medium text-foreground">{r.name}</p>
                      <p className="text-[10px] text-muted-foreground">{r.slug}</p>
                    </td>
                    <td className="p-3">
                      <Badge variant={r.is_active ? "default" : "destructive"} className="text-[9px]">
                        {r.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className="text-[9px]">{planName}</Badge>
                    </td>
                    <td className="p-3">
                      {isOnTrial ? (
                        <Badge className="text-[9px] bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {trialDaysLeft}d restantes
                        </Badge>
                      ) : r.trial_used ? (
                        <Badge variant="outline" className="text-[9px] text-muted-foreground">Usado</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] text-emerald-500 border-emerald-500/30">Disponível</Badge>
                      )}
                    </td>
                    <td className="p-3 hidden md:table-cell text-muted-foreground">
                      {loginInfo?.logged_in_at
                        ? new Date(loginInfo.logged_in_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </td>
                    <td className="p-3 hidden lg:table-cell">
                      {loginInfo?.ip_address ? (
                        <span className="font-mono text-muted-foreground text-[10px]">{loginInfo.ip_address}</span>
                      ) : "—"}
                    </td>
                    <td className="p-3 hidden xl:table-cell text-muted-foreground">
                      {topMethod ? formatPaymentMethod(topMethod[0]) : "—"}
                    </td>
                    <td className="p-3 hidden md:table-cell font-medium">{restOrders.length}</td>
                    <td className="p-3 hidden lg:table-cell font-medium">{formatCurrency(revenue)}</td>
                    <td className="p-3 text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString("pt-BR")}
                    </td>
                  </tr>
                );
              })}
              {filteredRestaurants.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-muted-foreground">
                    Nenhum restaurante encontrado com os filtros selecionados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">{filteredRestaurants.length} de {restaurants.length} contas exibidas</p>
      </motion.div>
    </div>
  );
};

function formatPaymentMethod(method: string): string {
  const map: Record<string, string> = {
    pix: "PIX",
    credit_card: "Cartão de Crédito",
    debit_card: "Cartão de Débito",
    cash: "Dinheiro",
    meal_voucher: "Vale Refeição",
    "não informado": "Não informado",
  };
  return map[method] || method;
}

export default AdminAccountsReportTab;
